/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

document.addEventListener('DOMContentLoaded', () => {
  // Handle search suggestion clicks (using event delegation for dynamic content)
  document.addEventListener('click', (e) => {
    const button = e.target.closest('.search-suggestion-button');
    if (!button) return;

    const terms = button.getAttribute('data-terms');
    if (!terms) return;

    // Try to find the closest form or any search form on the page
    const container = button.closest('.predictive-search-drawer') || button.closest('section') || document;
    const form = container.querySelector('form[action*="/search"]');

    if (form) {
      const input = form.querySelector('input[name="q"]');
      if (input) {
        input.value = terms;
        form.submit();
      }
    } else {
      // Fallback: direct redirect if form not found
      window.location.href = `/search?q=${encodeURIComponent(terms)}`;
    }
  });

  document.body.addEventListener('click', (e) => {
    const swatch = e.target.closest('.color-image-link');
    if (swatch) {
      const productItem = swatch.closest('product-item');
      if (!productItem) return;

      const newImgUrl = swatch.getAttribute('data-img-link');
      const newVariantUrl = swatch.getAttribute('data-variant-url');
      const newProductAvailable= swatch.getAttribute('data-vailable'); 

      const primaryImg = productItem.querySelector('.product-item__primary-image');
      if (primaryImg) {
        primaryImg.src = newImgUrl;
        primaryImg.srcset = '';
      }
      console.log(newProductAvailable); 
      if(newProductAvailable=='true'){
        productItem.classList.remove('product-item--sold-out'); 
      }else{ 
        productItem.classList.add('product-item--sold-out'); 
      }
      const productLinks = productItem.querySelectorAll('a[href*="/products/"]');
      productLinks.forEach(link => {
        link.href = newVariantUrl;
      });

      productItem.querySelectorAll('.color-image-link').forEach(item => item.classList.remove('active'));
      swatch.classList.add('active');

      e.preventDefault();
      e.stopPropagation();
    }
  });
  // Handle search suggestions visibility based on input value
  const updateSuggestionsVisibility = (input) => {
    const searchContainer = input.closest('.predictive-search') || input.closest('.predictive-search-drawer') || input.closest('section');
    const suggestions = searchContainer ? searchContainer.querySelector('.search_suggestion') : document.querySelector('.search_suggestion');

    if (suggestions) {
      if (input.value.trim() === '') {
        suggestions.removeAttribute('hidden');
      } else {
        suggestions.setAttribute('hidden', '');
      }
    }
  };

  // Listen for input events using delegation
  document.addEventListener('input', (e) => {
    if (e.target.matches('.predictive-search__input, .predictive-search__form input, input.predictive-search__form')) {
      updateSuggestionsVisibility(e.target);
    }
  });

  // Run initial check for all inputs already in the DOM
  document.querySelectorAll('.predictive-search__input, .predictive-search__form input, input.predictive-search__form').forEach(updateSuggestionsVisibility);
});

$(document).ready(function () {
  $('.footer__item.footer__item--links p.footer__item-title').on('click', function () {
    if ($(window).width() < 990) {
      $(this).toggleClass('active');
      $(this).siblings('.footer__item-content').slideToggle();
    }
  });
});

$(document).ready(function () {

  function mobileOwlInit() {
    $('.slide_in_mobile').each(function () {
      var $this = $(this);
      var mobileItems = $this.attr('data-mobile-items') || 1;
      var showArrows = $this.attr('data-show-arrows') === 'true';
      var showDots = $this.attr('data-show-dots') !== 'false';

      if ($(window).width() < 768) {
        if (!$this.hasClass('owl-loaded')) {
          $this.addClass('owl-carousel');
          $this.owlCarousel({
            items: mobileItems,
            dotsEach: 1,
            loop: true,
            margin: 15,
            slideBy: 1,
            nav: showArrows,
            dots: showDots
          });
        }
      } else {
        if ($this.hasClass('owl-loaded')) {
          $this.trigger('destroy.owl.carousel');
          $this.removeClass('owl-carousel owl-loaded');
          $this.find('.owl-stage-outer').children().unwrap();
        }
      }
    });
  }

  function customOwlInit() {
    $('.custom_owl_slider').each(function () {
      var $this = $(this);
      var sliderEnable = $this.attr('data-slider-enable'); // none, mobile, desktop, both
      var desktopItems = parseInt($this.attr('data-desktop-items')) || 4;
      var mobileItems = parseInt($this.attr('data-mobile-items')) || 1;
      var showDots = $this.attr('data-dots') !== 'false';

      var windowWidth = $(window).width();
      var shouldEnable = false;

      if (sliderEnable === 'both') {
        shouldEnable = true;
      } else if (sliderEnable === 'mobile' && windowWidth < 768) {
        shouldEnable = true;
      } else if (sliderEnable === 'desktop' && windowWidth >= 768) {
        shouldEnable = true;
      }

      if (shouldEnable) {
        if (!$this.hasClass('owl-loaded')) {
          $this.addClass('owl-carousel');
          $this.owlCarousel({
            items: desktopItems,
            loop: true,
            margin: 20,
            nav: false,
            dotsEach: 1,
            slideBy: 1,
            dots: showDots,
            responsive: {
              0: {
                items: mobileItems,
                margin: 15
              },
              768: {
                items: desktopItems
              }
            }
          });
        }
      } else {
        if ($this.hasClass('owl-loaded')) {
          $this.trigger('destroy.owl.carousel');
          $this.removeClass('owl-carousel owl-loaded');
          $this.find('.owl-stage-outer').children().unwrap();
        }
      }
    });
  }

  // Run on load
  mobileOwlInit();
  customOwlInit();

  // Run on resize
  $(window).resize(function () {
    mobileOwlInit();
    customOwlInit();
  });


  $(document).on('click', '.back-to-main-menu,.drawer__overlay', function () {
    $('.drawer__content collapsible-content[open]').removeAttr('open');
  });

});



   document.addEventListener('DOMContentLoaded', function() {
            var tabs = document.querySelectorAll('.sg_pop_el_cat li a');
            var descrs = document.querySelectorAll('.sg_pop_descr');
            
            if (tabs.length > 0 && descrs.length > 0) {
                tabs.forEach(function(tab) {
                    tab.addEventListener('click', function(e) {
                        e.preventDefault();
                        if (this.textContent === '|') return;
                        
                        var targetId = this.getAttribute('href');
                        
                        // Hide all description sections
                        descrs.forEach(function(descr) {
                            descr.style.display = 'none';
                            descr.classList.remove('active');
                        });
                        
                        // Remove active class from all tabs except separators
                        tabs.forEach(function(t) {
                            if (t.textContent !== '|') {
                                t.classList.remove('active');
                                t.parentElement.classList.remove('active');
                            }
                        });
                        
                        // Show target description section
                        if (targetId) {
                            var target = document.querySelector(targetId);
                            if (target) {
                                target.style.display = 'block';
                                target.classList.add('active');
                            }
                        }
                        
                        // Add active class to clicked tab and its parent li
                        this.classList.add('active');
                        this.parentElement.classList.add('active');
                    });
                });
            }
        });
 